import { useRoute } from "@react-navigation/native";
import { RouteProp } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { format } from "date-fns";
import { useAtom } from "jotai";
import { isNil } from "lodash";
import { useCallback } from "react";
import { useFragment } from "react-relay";

import { setFiltersFromStorage } from "@/components/AgendaFilters/utils/setFiltersFromStorage";
import {
  facilityToScheduleOption,
  ScheduleOption,
  ScheduleSwitcher,
} from "@/components/ScheduleSwitcher";
import { useFacilityStartEndTime } from "@/features/Facility";
import { useFacilityGroupSize } from "@/features/Facility/hooks/useFacilityGroupSize";
import { useScheduleNavigator } from "@/hooks/useScheduleNavigator";
import { VStack } from "@whoosh/design-system/src/components/primitive";

import {
  TimeOfDayFilterControl,
  GroupSizeFilterControl,
} from "../../../../components/AgendaFilters";
import { AgendaDateSelectorControl } from "../../../../components/AgendaFilters/components/DateSelectorControl/AgendaDateSelectorControl";
import { AgendaWaitlist } from "../../../../components/AgendaWaitlist";
import { ClubStackNavigatorParamList } from "../../../../navigators/ClubNavigator/ClubStackNavigator";
import {
  isBookingMultipleTeeTimesAtom,
  teeSheetGroupSizeFilterAtom,
  teeSheetHoleFilterAtom,
  teeSheetListFilterAtom,
  teeSheetTimesAtom,
} from "../../atoms";
import {
  TeeSheetHoleCountSelectorControl,
  TeeSheetCourseSelectorControl,
  TeeSheetMultipleBookingToggle,
  TeeSheetViewFilterRadioGroup,
} from "../TeeSheetBasedFilters";
import { HoleCount } from "../TimeSlotList/components/__generated__/TimeSlotListInternalQuery.graphql";
import { TeeSheetAgendaSidebarFragment_facility$key } from "./__generated__/TeeSheetAgendaSidebarFragment_facility.graphql";
import { TeeSheetAgendaSidebar_session$key } from "./__generated__/TeeSheetAgendaSidebar_session.graphql";

type Props = {
  facilityRef: TeeSheetAgendaSidebarFragment_facility$key | null;
  sessionRef: TeeSheetAgendaSidebar_session$key | null; // TODO: This shouldn't be a fragment ref from another file
  selectedDate: string;
  showWaitlist: boolean;
  selectedCourseIds: string[];
  setSelectedCourseIds: (courseIds: string[]) => void;
  dateOverride?: string;
};

export const TeeSheetAgendaSidebar = ({
  facilityRef,
  sessionRef,
  showWaitlist,
  selectedCourseIds,
  selectedDate,
  setSelectedCourseIds,
  dateOverride,
}: Props) => {
  const session = useFragment(
    TeeSheetAgendaSidebarSessionFragmentString,
    sessionRef
  );
  const facility = useFragment(
    TeeSheetAgendaSidebarFragmentString,
    facilityRef
  );

  const facilityStartEndTime = useFacilityStartEndTime(facility ?? null);
  const facilityGroupSizes = useFacilityGroupSize(facility ?? null);

  const [teeSheetGroupSizeFilter, setTeeSheetGroupSizeFilter] = useAtom(
    teeSheetGroupSizeFilterAtom
  );

  const [teeSheetListFilter, setTeeSheetListFilter] = useAtom(
    teeSheetListFilterAtom
  );

  const [isBookingMultipleTeeTimes, setIsBookingMultipleTeeTimes] = useAtom(
    isBookingMultipleTeeTimesAtom
  );

  const [teeSheetTimes, setTeeSheetTimes] = useAtom(teeSheetTimesAtom);

  const isPrivate = facility?.club?.supportsPrivate;

  const [teeSheetHoleFilter, setTeeSheetHoleFilter] = useAtom(
    teeSheetHoleFilterAtom
  );

  const onTimeOfDayChange = useCallback(
    ({ timeLTE, timeGTE }: { timeLTE?: string; timeGTE?: string }) => {
      if (!timeLTE && !timeGTE) {
        setTeeSheetTimes(undefined);
      } else {
        setTeeSheetTimes({ timeLTE, timeGTE });
      }

      if (facility?.id && timeGTE && timeLTE) {
        setFiltersFromStorage(
          `x-whoosh-${facility.id}-time-of-day-start-time`,
          timeGTE.toString()
        );
        setFiltersFromStorage(
          `x-whoosh-${facility.id}-time-of-day-end-time`,
          timeLTE.toString()
        );
      }
    },
    [facility?.id, setTeeSheetTimes]
  );

  const { date: currentlySelectedDate } =
    useRoute<RouteProp<ClubStackNavigatorParamList, "Agenda">>().params;

  const date = dateOverride || currentlySelectedDate;

  const onGroupSizeChange = (value: number | undefined) => {
    if (!isNil(value)) {
      setTeeSheetGroupSizeFilter(value);
      setFiltersFromStorage(
        `x-whoosh-${facility?.id}-group-size-filter`,
        value.toString()
      );
    }
  };

  const onHoleCountChange = (value: HoleCount | null) => {
    setTeeSheetHoleFilter(value);
    if (value) {
      setFiltersFromStorage(
        `x-whoosh-${facility?.id}-hole-count-filter`,
        value
      );
    }
  };

  const navigateToSchedule = useScheduleNavigator();

  const onScheduleSwitcherChange = (option: ScheduleOption) => {
    const curDate = new Date();
    const todayString = format(curDate, "yyyy-MM-dd");
    const navigateDate = date ? date : todayString;
    navigateToSchedule(option.type, option.slug, navigateDate);
  };

  const selectedScheduleOption = facilityToScheduleOption(facility ?? null);

  return (
    <VStack gap={5} px={2}>
      <ScheduleSwitcher
        selectedOption={selectedScheduleOption}
        onChange={onScheduleSwitcherChange}
        sessionRef={session ?? null}
      />
      <TimeOfDayFilterControl
        controlStartTime={facilityStartEndTime?.startTime}
        controlEndTime={facilityStartEndTime?.endTime}
        onChange={onTimeOfDayChange}
        selectedTimeRange={teeSheetTimes}
      />
      {(isPrivate && teeSheetListFilter === "Available") || !isPrivate ? (
        <GroupSizeFilterControl
          controlMinGroupSize={facilityGroupSizes.min}
          controlMaxGroupSize={facilityGroupSizes.max}
          onChange={onGroupSizeChange}
          selectedGroupSize={teeSheetGroupSizeFilter}
        />
      ) : null}
      <AgendaDateSelectorControl
        facilityRef={facility ?? null}
        selectedDate={selectedDate}
      />
      {isPrivate ? (
        <TeeSheetViewFilterRadioGroup
          value={teeSheetListFilter}
          onChange={setTeeSheetListFilter}
        />
      ) : null}
      <TeeSheetCourseSelectorControl
        facilityRef={facility ?? null}
        onChange={setSelectedCourseIds}
        courseIds={selectedCourseIds}
      />
      {/* We only show the hole filter on public clubs */}
      {!isPrivate ? (
        <TeeSheetHoleCountSelectorControl
          value={teeSheetHoleFilter}
          onChange={onHoleCountChange}
          facilityRef={facility}
        />
      ) : null}
      <TeeSheetMultipleBookingToggle
        facilityRef={facility ?? null}
        onChange={setIsBookingMultipleTeeTimes}
        isBookingMultipleTeeTimes={isBookingMultipleTeeTimes}
      />
      {showWaitlist ? (
        <AgendaWaitlist
          date={date ?? ""}
          facilityId={facility?.id ?? ""}
          courseIds={selectedCourseIds}
        />
      ) : null}
    </VStack>
  );
};

export const TeeSheetAgendaSidebarFragmentString = graphql`
  fragment TeeSheetAgendaSidebarFragment_facility on Facility {
    id
    club {
      supportsPrivate
    }
    ...useFacilityStartEndTime_facility
    ...useFacilityGroupSize_facility
    ...TeeSheetMultipleBookingToggleFragment_Facility
    ...TeeSheetCourseSelectorControlFragment_facility
    ...AgendaDateSelectorControlFragment_facility
    ...facilityToScheduleOption_facility
    ...TeeSheetHoleCountSelectorControl_facility
  }
`;

export const TeeSheetAgendaSidebarSessionFragmentString = graphql`
  fragment TeeSheetAgendaSidebar_session on Session {
    ...ScheduleSwitcher_session
  }
`;
